// ---------------------------------------------------------------------------
// MultinomialTool  ―  interactive multinomial-coefficient explorer
//
// Props
//   m        number   • # categories ( ≥ 3 ; default 3 )
//   n        number   • total objects  ( default 6 )
//   showProb boolean  • if true, also show probability assuming equal p = 1/m
//   style    object   • optional inline CSS
//
// Usage
//   <MultinomialTool m={4} n={8} showProb />
// ---------------------------------------------------------------------------

import React, { useState, useMemo } from 'react';

/* ---------- tiny factorial helper (can be reused project-wide) ---------- */
function factorial(k) {
  if (k < 0) return NaN;
  let f = 1;
  for (let i = 2; i <= k; i++) f *= i;
  return f;
}

export default function MultinomialTool({
  m = 3,
  n = 6,
  showProb = false,
  style = {}
}) {
  /* ----- 1. state: partition vector [n1, n2, …, nm] ------------------- */
  const init = Array.from({ length: m }, (_, i) => (i === 0 ? n : 0));
  const [parts, setParts] = useState(init);

  /* helper to set one n_i while keeping the total ≤ n ------------------ */
  const setPart = (idx, value) => {
    const v = Math.max(0, Math.floor(value));
    const next = [...parts];
    next[idx] = v;
    if (next.reduce((s, x) => s + x, 0) <= n) setParts(next);
  };

  const total    = parts.reduce((s, x) => s + x, 0);
  const remaining = n - total;

  /* ----- 2. derived values (memoised) --------------------------------- */
  const coeff = useMemo(() => {
    if (remaining !== 0) return NaN;          // invalid until all assigned
    const denom = parts.reduce((prod, k) => prod * factorial(k), 1);
    return factorial(n) / denom;
  }, [n, parts, remaining]);

  const equalProb = useMemo(() => {
    if (remaining !== 0 || !showProb) return NaN;
    const p = 1 / m; // equal probability for each category
    return coeff * p ** n;
  }, [coeff, n, m, remaining, showProb]);

  /* ----- 3. render ----------------------------------------------------- */
  return (
    <div
      style={{
        border: '1px solid #ccc',
        padding: 14,
        maxWidth: 400,
        background: '#fafafa',
        fontFamily: 'sans-serif',
        ...style
      }}
    >
      <h3 style={{ margin: 0 }}>Multinomial Coefficient Tool</h3>

      <p style={{ margin: '6px 0' }}>
        total&nbsp;<strong>n = {n}</strong> | categories&nbsp;<strong>m = {m}</strong>
      </p>

      {/* inputs */}
      {parts.map((k, i) => (
        <label key={i} style={{ display: 'block', marginBottom: 6 }}>
          n<sub>{i + 1}</sub>&nbsp;
          <input
          className="inputBox"
            type="number"
            min={0}
            max={n}
            value={k}
            onChange={e => setPart(i, e.target.value)}
            style={{ width: 70 }}
          />
        </label>
      ))}

      <p style={{ fontSize: 24, margin: '6px 0' }}>
        Remaining to assign:&nbsp;<strong>{remaining}</strong>
      </p>

      <hr />

      <p style={{ margin: '6px 0' }}>
        <strong>C(n; n₁,…,n<sub>{m}</sub>) = </strong>
        {Number.isFinite(coeff) ? coeff.toLocaleString() : '—'}
      </p>

      {showProb && Number.isFinite(equalProb) && (
        <p style={{ margin: '6px 0' }}>
          <strong>P(n₁,…,n<sub>{m}</sub>) with p = 1/m:&nbsp;</strong>
          {equalProb.toPrecision(4)}
        </p>
      )}

      {remaining !== 0 && (
        <p style={{ color: 'crimson', fontSize: 12, marginTop: 4 }}>
          (All parts must add up to&nbsp;{n}&nbsp;for a valid coefficient.)
        </p>
      )}
    </div>
  );
}
